import { Box, Button, Heading, LinkBox, LinkOverlay, Text, VStack } from "@biblioteksentralen/react";
import { getPath, ResolvedSommerlesBannerData } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import onDesktop from "../../utils/onDesktop";
import { SanityImagePreservingAspectRatio } from "../images/SanityImagePreservingAspectRatio";
import { formatSommerlesDate } from "./utils";
import { ExternalLink } from "react-feather";
import Character from "./Character";
import { SanityImageFillAvailableSpace } from "../images/SanityImageFillAvailableSpace";
import { useState } from "react";
import { getContainerQuery } from "../site/banners/promotedItems/utils";
import useBreakpointValue from "../../utils/hooks/useBreakpointValue";

type BannerContentUnderwayProps = {
  sommerles: ResolvedSommerlesBannerData;
  smallScreen: boolean;
  onLandingPage: boolean;
};

export const BannerContentUnderway = (props: BannerContentUnderwayProps) => {
  const { t, lang } = useTranslation();
  const sommerles = props.sommerles;
  if (!sommerles?.templateContent || !sommerles.startDate || !sommerles.endDate) return null;

  return (
    <>
      <UnderwayAssetLayout {...props} />
      <VStack
        as={LinkBox}
        position="absolute"
        display="flex"
        justifyContent="flex-end"
        width="100%"
        height="100%"
        sx={getContainerQuery({
          base: { padding: "1rem 0" },
          md: { padding: "2rem 0" },
        })}
      >
        <Heading as="h2" visibility="hidden">
          {`${t("Sommerles")} 2025 - ${t("Gåterullmysteriene")}`}
        </Heading>
        {!props.onLandingPage && (
          <Text
            textAlign="center"
            zIndex={1000}
            color="#6D1F49"
            sx={{ WebkitTextStroke: "3px #F0FEFD", paintOrder: "stroke fill" }}
            fontSize={{ base: "xl", md: "3xl" }}
            fontWeight={600}
          >
            {formatSommerlesDate(lang, sommerles.startDate)}
            {" - "}
            {formatSommerlesDate(lang, sommerles.endDate)}
          </Text>
        )}
        {props.onLandingPage && (
          <LinkOverlay
            href={sommerles?.separatePage ? getPath(sommerles) : `${sommerles?.templateContent?.url!}/?lang=${lang}`}
          >
            <Button
              as="span"
              background="#4C1433"
              color="#F3CC6A"
              marginTop={{ base: "0.5rem", md: "unset" }}
              fontSize={{ base: "md", md: "lg" }}
              padding={{ base: "0.25rem 1rem", md: "0.5rem 1rem" }}
              border="2px solid #F3CC6A"
              _hover={{}}
              _groupHover={onDesktop({ filter: "brightness(0.85)" })}
              transition="filter 0.3s ease"
              rightIcon={
                sommerles?.separatePage ? undefined : (
                  <Box width="0.75rem" transform="translateY(0.125rem)">
                    <ExternalLink width="100%" />
                  </Box>
                )
              }
            >
              {t("Les om")} {t("Sommerles")}
            </Button>
          </LinkOverlay>
        )}
      </VStack>
    </>
  );
};

const UnderwayAssetLayout = ({ sommerles, smallScreen, onLandingPage }: BannerContentUnderwayProps) => {
  const { ts } = useTranslation();
  const [backgroundHasLoaded, setBackgroundHasLoaded] = useState<boolean>(false);
  const baseScreenDisplay = useBreakpointValue({ base: "block", sm: "none" });
  const smallScreenDisplay = useBreakpointValue({ base: "none", sm: "block", lg: "none" });
  const bigScreenDisplay = useBreakpointValue({ base: "none", lg: "block" });
  if (!sommerles?.templateContent || !sommerles.startDate || !sommerles.endDate) return null;
  const logo = sommerles.templateContent.logo;
  const title = sommerles.templateContent.title;
  const character3Image = sommerles.templateContent.detail5;
  const detail = sommerles.templateContent.detail1;
  const backgroundImage = sommerles.templateContent.backgroundImage;

  return (
    <Box
      position="absolute"
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="space-between"
      borderRadius={{ base: 0, md: "0.5rem" }}
      overflow={{ base: "hidden", md: onLandingPage ? "hidden" : "visible" }}
      isolation="isolate"
    >
      <Box
        position="relative"
        width="100%"
        height="100%"
        borderRadius={{ base: 0, md: "0.5rem" }}
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box position="absolute" width="100%" height="100%">
          <SanityImageFillAvailableSpace
            image={backgroundImage}
            resolution={1000}
            quality={100}
            _groupHover={{}}
            imageWrapperProps={{ borderRadius: 0 }}
            customNextImageProps={{
              onLoad: () => setBackgroundHasLoaded(true),
            }}
          />
        </Box>
        {backgroundHasLoaded && (
          <>
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              sx={getContainerQuery({
                base: { width: "230%", transform: "translate(0, -27%)" },
                sm: { width: "135%", transform: "translate(0, -7%)" },
                md: { width: "102%" },
              })}
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              sx={getContainerQuery({
                base: { width: "240%", transform: "translate(0, -30%) rotate(0.5turn)" },
                sm: { width: "140%", transform: "translate(0, -5%) rotate(0.5turn)" },
                md: { width: "103%", transform: "translate(4%, -10%) rotate(0.5turn)" },
              })}
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              sx={getContainerQuery({
                base: { width: "220%", transform: "translate(0, -35%) scale(-1, 1)" },
                sm: { width: "130%", transform: "translate(0, -10%) scale(-1, 1)" },
                md: { width: "97%" },
              })}
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              sx={getContainerQuery({
                base: { width: "200%", transform: "translate(0, -10%) rotate(0.5turn)" },
                sm: { width: "120%", transform: "translate(0, 0%) rotate(0.5turn)" },
                md: { width: "85%", transform: "translate(0, 10%) rotate(0.5turn)" },
              })}
              boxShadow="none"
            />
            <SanityImagePreservingAspectRatio
              image={detail}
              resolution={1000}
              position="absolute"
              sx={getContainerQuery({
                base: { width: "170%", transform: "translate(5%, -50%) scale(-1, 1) rotate(0.5turn)" },
                sm: { width: "100%", transform: "translate(5%, -25%) scale(-1, 1) rotate(0.5turn)" },
                md: { width: "93%", transform: "translate(-5%, -20%) scale(-1, 1) rotate(0.5turn)" },
              })}
              boxShadow="none"
            />
            <Box
              position="absolute"
              display="flex"
              flexDirection={smallScreen ? "column" : "column-reverse"}
              alignItems="center"
              sx={getContainerQuery({
                base: { width: "85%", transform: "translate(0, -30%)" },
                sm: { width: "65%", transform: "translate(0, -20%)" },
                md: { transform: "translate(0, -10%)" },
              })}
            >
              <SanityImagePreservingAspectRatio
                image={ts(title)}
                resolution={1000}
                boxShadow="none"
                alt="Sommerles"
                width="100%"
                marginBottom={smallScreen ? "-1.5rem" : 0}
              />
              <SanityImagePreservingAspectRatio
                image={ts(logo)}
                resolution={1000}
                boxShadow="none"
                alt="Sommerles"
                width="85%"
                marginBottom={smallScreen ? 0 : "-1rem"}
              />
            </Box>
          </>
        )}
      </Box>
      <Box position="relative" width="100%" display="flex" alignItems="center" justifyContent="center">
        {backgroundHasLoaded && (
          <>
            <Box width="50%" left="75%" transform="translate(0, 5%)" position="absolute" display={baseScreenDisplay}>
              <Character zIndex={10} image={character3Image} position="relative" />
            </Box>
            <Box
              transform="translate(0, -5%)"
              position="absolute"
              display={smallScreenDisplay}
              sx={getContainerQuery({
                base: { width: "55%", left: "70%" },
                sm: { width: "45%", left: "77%" },
              })}
            >
              <Character zIndex={10} image={character3Image} position="relative" />
            </Box>
            <Box
              sx={getContainerQuery({
                base: { width: "90%", left: "52%" },
                md: { width: "35%", left: "78%", transform: "translate(0, -7%)" },
              })}
              width="35%"
              left="78%"
              transform="translate(0, -7%)"
              position="absolute"
              display={bigScreenDisplay}
            >
              <Character zIndex={10} image={character3Image} position="relative" />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
